import type { RaceConfig } from '../../types/config.ts';
import type { FinanceResult, CandidateFinance } from '../../types/finance.ts';
import type { ProviderState } from '../../types/ui.ts';
import { candidateColor } from '../../lib/colors.ts';
import { fmtCurrency, ratio } from '../../lib/format.ts';
import { CandidateToken } from '../common/CandidateToken.tsx';
import { Provenance } from '../common/Provenance.tsx';
import { SourceState } from '../common/SourceState.tsx';

function financeFor(fr: FinanceResult | null, id: string): CandidateFinance | undefined {
  return fr?.candidates.find((c) => c.candidateId === id);
}

function spendLabel(support: number, oppose: number): string {
  const forStr = `${fmtCurrency(support)} for`;
  return oppose > 0 ? `${forStr} · ${fmtCurrency(oppose)} against` : forStr;
}

/** One labeled, scaled bar for a single source (reported OR FEC-filed). */
function MoneyBar({
  name,
  badge,
  support,
  oppose,
  max,
  color,
  muted = false,
}: {
  name: string;
  badge: 'seed' | 'live';
  support: number;
  oppose: number;
  max: number;
  color: string;
  muted?: boolean;
}) {
  return (
    <div className="money-metric">
      <div className="money-metric__head">
        <span className="money-metric__name">{name}</span>
        <span className={`badge badge--${badge}`}>{badge === 'seed' ? 'reported' : 'live'}</span>
        <span className="money-metric__fig tabular">{spendLabel(support, oppose)}</span>
      </div>
      <div className={`money-row__bar${muted ? ' money-row__bar--muted' : ''}`} aria-hidden="true">
        <div
          className="money-row__fill"
          style={{ width: `${ratio(support, max) * 100}%`, ['--cand-color' as string]: color }}
        />
      </div>
    </div>
  );
}

/** Outside (independent-expenditure) spending. Two clearly-labeled bars per
 *  candidate on a SHARED scale — "Reported" (widely-reported outside spending,
 *  from config seed) and "FEC-filed to date" (live from the FEC). Side by side,
 *  the gap is explicit: the live FEC by_candidate aggregate is precise but
 *  partial (large SuperPAC filings lag), so it reads as a fraction of the
 *  reported total instead of being mistaken FOR it (or vice-versa). */
export function MoneyPanel({
  race,
  financeState,
  resolved,
}: {
  race: RaceConfig;
  financeState: ProviderState<FinanceResult>;
  resolved: 'dark' | 'light';
}) {
  const live = financeState.status === 'success' ? financeState.data : null;
  const seed = race.financeSeed ?? null;

  if (!seed && !live) {
    return (
      <SourceState
        label="Money"
        kind={financeState.status === 'loading' ? null : financeState.errorKind}
        loading={financeState.status === 'loading' || financeState.status === 'idle'}
      />
    );
  }

  // Shared scale across BOTH sources so reported-vs-filed is an honest comparison.
  const maxSpend = Math.max(
    1,
    ...race.candidates.flatMap((c) => [
      financeFor(seed, c.id)?.supportTotal ?? 0,
      financeFor(live, c.id)?.supportTotal ?? 0,
    ]),
  );

  return (
    <div className="money">
      <div className="money__rows">
        {race.candidates.map((cand) => {
          const color = candidateColor(cand, resolved);
          const s = financeFor(seed, cand.id);
          const l = financeFor(live, cand.id);
          const top = l?.byGroup[0] ?? s?.byGroup[0];
          return (
            <div key={cand.id} className="money-row">
              <div className="money-row__head">
                <CandidateToken candidate={cand} color={color} />
              </div>

              {seed ? (
                <MoneyBar
                  name="Reported"
                  badge="seed"
                  support={s?.supportTotal ?? 0}
                  oppose={s?.opposeTotal ?? 0}
                  max={maxSpend}
                  color={color}
                  muted
                />
              ) : null}

              {live ? (
                <MoneyBar
                  name="FEC-filed to date"
                  badge="live"
                  support={l?.supportTotal ?? 0}
                  oppose={l?.opposeTotal ?? 0}
                  max={maxSpend}
                  color={color}
                />
              ) : null}

              {top ? (
                <p className="money-row__group dim">
                  Top: {top.committee} ({fmtCurrency(top.amount)}, {top.indicator})
                </p>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="money__sources">
        {seed ? <Provenance source={seed.source} fetchedAt={seed.fetchedAt} seeded /> : null}
        {live ? <Provenance source="FEC (filed to date)" fetchedAt={live.fetchedAt} /> : null}
      </div>

      {!live && financeState.status === 'error' ? (
        <p className="muted money__fallback dim">
          FEC-filed totals unavailable
          {financeState.errorKind === 'not_configured'
            ? ' — add FEC candidate ids to the race config.'
            : ' — showing reported figures only.'}
        </p>
      ) : null}

      <p className="money__legend dim">
        <strong>Reported</strong> = widely-reported outside spending. <strong>FEC-filed</strong> =
        independent expenditures on file with the FEC to date (partial — large SuperPAC filings lag).
      </p>
    </div>
  );
}
